import React, { createContext, useEffect, useState, useRef, useCallback } from 'react';
import { IP } from '../components/utils';

// Module-level singleton to prevent multiple WebSocket connections
let globalSocketInstance = null;
let globalIsConnecting = false;

export const WebSocketContext = createContext();
// Chat socket functionality commented out - exporting dummy context to prevent import errors
export const AdminSocket = createContext({
  admin: [],
  sendAdminMessage: () => { },
  connectAdminSocket: () => { },
  setAdmin: () => { },
  disconnectAdminSocket: () => { },
  isAdminConnected: false,
  getAdminConnectionStatus: () => false,
  currentAdminChatId: null,
  manuallyDisconnected: false,
  resetManualDisconnect: () => { },
});

export const WebSocketProvider = ({ children }) => {
  const [messages, setMessages] = useState([]);
  const socketRef = useRef(null);
  // const [admin, setAdmin] = useState([]);
  // const adminSocketRef = useRef(null);
  // const [isAdminConnected, setIsAdminConnected] = useState(false);
  // const [currentAdminChatId, setCurrentAdminChatId] = useState(null);
  // const [manuallyDisconnected, setManuallyDisconnected] = useState(false);

  // const adminPingIntervalRef = useRef(null);
  // const adminReconnectIntervalRef = useRef(null);
  const clientReconnectIntervalRef = useRef(null);
  const clientPingIntervalRef = useRef(null);
  const connectClientSocketRef = useRef(null);
  const isConnectingRef = useRef(false);

  // ----------------------------- Client WebSocket Management -----------------------------

  const stopClientPingInterval = useCallback(() => {
    if (clientPingIntervalRef.current) {
      clearInterval(clientPingIntervalRef.current);
      clientPingIntervalRef.current = null;
    }
  }, []);

  const stopClientReconnectInterval = useCallback(() => {
    if (clientReconnectIntervalRef.current) {
      clearInterval(clientReconnectIntervalRef.current);
      clientReconnectIntervalRef.current = null;
      console.log("Stopped client reconnect interval");
    }
  }, []);

  const startClientPingInterval = useCallback(() => {
    if (clientPingIntervalRef.current) {
      console.warn("Client ping interval already running. Skipping new interval.");
      return;
    }

    clientPingIntervalRef.current = setInterval(() => {
      const socket = socketRef.current;
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: "ping" }));
      } else {
        console.warn("Cannot send client ping - WebSocket not open");
        stopClientPingInterval();
      }
    }, 30000);
  }, [stopClientPingInterval]);

  const startClientReconnectInterval = useCallback(() => {
    if (clientReconnectIntervalRef.current) {
      console.warn("Client reconnect interval already running. Skipping new interval.");
      return;
    }

    console.log("Starting client reconnect interval");

    clientReconnectIntervalRef.current = setInterval(() => {
      const socket = socketRef.current;
      // Only reconnect if socket is closed (CLOSED = 3) or doesn't exist
      // Don't reconnect if CONNECTING (0) or OPEN (1) or CLOSING (2)
      if (!socket || socket.readyState === WebSocket.CLOSED) {
        console.log("Client connection lost, attempting to reconnect");
        if (connectClientSocketRef.current) {
          connectClientSocketRef.current();
        }
      }
    }, 10000); // Check every 10 seconds
  }, []);

  // ----------------------------- Client WebSocket -----------------------------

  const connectClientSocket = useCallback(() => {
    // RSM-CHATBOT-007: the WebSocket authenticates via the HttpOnly cookie (sent
    // automatically on the same-origin handshake); gate on the non-sensitive
    // auth flag rather than a readable token.
    const authed = localStorage.getItem("isAuthed") || null;
    if (!authed) {
      console.warn("Not authenticated, skipping WebSocket connection.");
      return;
    }

    // Global singleton check - prevent multiple connections across all provider instances
    if (globalIsConnecting) {
      console.log("Global WebSocket connection already in progress, skipping");
      return;
    }

    if (globalSocketInstance) {
      const globalState = globalSocketInstance.readyState;
      if (globalState === WebSocket.OPEN) {
        console.log("Global WebSocket already connected, reusing existing connection");
        socketRef.current = globalSocketInstance;
        return;
      }
      if (globalState === WebSocket.CONNECTING) {
        console.log("Global WebSocket already connecting, skipping new connection");
        socketRef.current = globalSocketInstance;
        isConnectingRef.current = true;
        return;
      }
      // Only close if in CLOSING or CLOSED state
      if (globalState === WebSocket.CLOSING || globalState === WebSocket.CLOSED) {
        globalSocketInstance.close();
        globalSocketInstance = null;
      }
    }

    // Prevent concurrent connection attempts (local check)
    if (isConnectingRef.current) {
      console.log("WebSocket connection already in progress, skipping");
      return;
    }

    // Check if already connected or connecting (local check)
    const existingSocket = socketRef.current;
    if (existingSocket) {
      if (existingSocket.readyState === WebSocket.OPEN) {
        console.log("WebSocket already connected, skipping new connection");
        return;
      }
      if (existingSocket.readyState === WebSocket.CONNECTING) {
        console.log("WebSocket already connecting, skipping new connection");
        isConnectingRef.current = true;
        return;
      }
      // Only close if in CLOSING or CLOSED state
      if (existingSocket.readyState === WebSocket.CLOSING || existingSocket.readyState === WebSocket.CLOSED) {
        existingSocket.close();
      }
    }

    isConnectingRef.current = true;
    globalIsConnecting = true;
    // Use ws:// for localhost, wss:// for production
    const wsProtocol = IP.includes('localhost') || IP.includes('127.0.0.1') ? 'ws' : 'wss';
    // Auth via HttpOnly cookie on the same-origin handshake. ?token= kept only
    // as a transitional fallback if a legacy token is still present.
    const legacyToken = null;
    const WS_URL = legacyToken
      ? `${wsProtocol}://${IP}/ws/analytics/?token=${encodeURIComponent(legacyToken)}`
      : `${wsProtocol}://${IP}/ws/analytics/`;
    const ws = new WebSocket(WS_URL);
    socketRef.current = ws;
    globalSocketInstance = ws;

    ws.onopen = () => {
      console.log("Client WebSocket connected");
      isConnectingRef.current = false; // Reset connecting flag
      globalIsConnecting = false; // Reset global connecting flag
      stopClientReconnectInterval(); // Stop reconnect interval when connected
      startClientPingInterval();
      // NOW request the initial data
      ws.send(JSON.stringify({
        route: 'get_initial_data'
      }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // 🔹 Reply to server heartbeat
        if (data.type === "ping") {
          ws.send(JSON.stringify({ type: "pong" }));
          return; // don't push ping/pong messages into messages list
        }

        // 🔹 Handle active sessions
        if (data.active_sessions && Array.isArray(data.active_sessions)) {
          // Active sessions update
        }

        // 🔹 Store only relevant messages - allow partial updates
        const isRelevantMessage = (msg) =>
          (msg.interaction && msg.sentiments) ||
          msg.active_sessions ||
          msg.customers ||
          msg.type === "ping";

        if (isRelevantMessage(data)) {
          setMessages((prev) => [...prev, data]);
        }
      } catch (error) {
        console.error("Error parsing message:", error);
      }
    };

    ws.onclose = (event) => {
      console.warn("Client WebSocket disconnected:", event.code, event.reason);
      isConnectingRef.current = false; // Reset connecting flag
      globalIsConnecting = false; // Reset global connecting flag
      if (globalSocketInstance === ws) {
        globalSocketInstance = null; // Clear global instance
      }
      stopClientPingInterval();
      // Start reconnection interval if not manually disconnected
      startClientReconnectInterval();
    };

    ws.onerror = (event) => {
      console.error("Client WebSocket error:", event);
      isConnectingRef.current = false; // Reset connecting flag
      globalIsConnecting = false; // Reset global connecting flag
      if (globalSocketInstance === ws) {
        globalSocketInstance = null; // Clear global instance
      }
      stopClientPingInterval();
    };
  }, [startClientPingInterval, stopClientPingInterval, stopClientReconnectInterval, startClientReconnectInterval]);

  // Store the function in ref to avoid circular dependency
  connectClientSocketRef.current = connectClientSocket;

  useEffect(() => {
    connectClientSocket();

    return () => {
      stopClientPingInterval();
      stopClientReconnectInterval();
      isConnectingRef.current = false; // Reset connecting flag on cleanup
      if (socketRef.current) {
        // Only clear global instance if this is the active connection
        if (globalSocketInstance === socketRef.current) {
          globalIsConnecting = false;
          // Don't close if there might be other providers using it
          // Only close if we're sure this is the last one
          const shouldClose = socketRef.current.readyState === WebSocket.OPEN ||
            socketRef.current.readyState === WebSocket.CONNECTING;
          if (shouldClose) {
            socketRef.current.close();
          }
          globalSocketInstance = null;
        } else {
          // This is a local socket, safe to close
          if (socketRef.current.readyState === WebSocket.OPEN ||
            socketRef.current.readyState === WebSocket.CONNECTING) {
            socketRef.current.close();
          }
        }
        socketRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps - only run once on mount


  // ----------------------------- Admin WebSocket -----------------------------
  // COMMENTED OUT - Chat socket functionality

  // const connectAdminSocket = (chatId) => {
  //   console.log("Connecting admin WebSocket with chatId:", chatId);

  //   // If already connected to the same chat, don't reconnect
  //   if (adminSocketRef.current && currentAdminChatId === chatId) {
  //     console.log("Already connected to this chat:", chatId);
  //     return;
  //   }

  //   // If connected to a different chat, disconnect first
  //   if (adminSocketRef.current && currentAdminChatId !== chatId) {
  //     console.log("Disconnecting from previous chat:", currentAdminChatId);
  //     disconnectAdminSocket();
  //   }

  //   const token = null;
  //   if (!token) {
  //     console.warn("No token found.");
  //     return;
  //   }

  //   const ADMIN_WS = `wss://${IP}/ws/chat/?chat_id=${chatId}&token=${encodeURIComponent(token)}`;
  //   const qws = new WebSocket(ADMIN_WS);
  //   adminSocketRef.current = qws;
  //   setCurrentAdminChatId(chatId);

  //   qws.onopen = () => {
  //     console.log("Admin WebSocket connected successfully to chat:", chatId);
  //     setIsAdminConnected(true);
  //     setManuallyDisconnected(false); // Reset manual disconnect flag
  //     startAdminPingInterval();
  //     startAdminReconnectInterval(chatId);

  //     // Send admin connection notification
  //     const adminName = localStorage.getItem('username') || 'Admin';
  //     const connectionPayload = {
  //       route: "admin_connected",
  //       sender: "Admin",
  //       admin_name: adminName,
  //       chat_id: chatId
  //     };
  //     console.log("Sending admin connection notification:", connectionPayload);
  //     qws.send(JSON.stringify(connectionPayload));
  //   };

  //   qws.onmessage = (event) => {
  //     try {
  //       const data = JSON.parse(event.data);

  //       if (data.route === "pong") {
  //         // console.log("Received pong from server");
  //         return;
  //       }

  //       setAdmin((prev) => [...prev, data]);
  //     } catch (error) {
  //       console.error("Error parsing admin message:", error);
  //     }
  //   };

  //   qws.onclose = () => {
  //     console.warn("Admin WebSocket closed for chat:", chatId);
  //     setIsAdminConnected(false);
  //     stopAdminPingInterval();
  //     adminSocketRef.current = null;
  //     setCurrentAdminChatId(null);
  //     // Start reconnection interval even after close
  //     if (chatId) {
  //       startAdminReconnectInterval(chatId);
  //     }
  //   };

  //   qws.onerror = (err) => {
  //     console.error("Admin WebSocket error", err);
  //     setIsAdminConnected(false);
  //     stopAdminPingInterval();
  //     adminSocketRef.current = null;
  //     setCurrentAdminChatId(null);
  //     // Start reconnection interval even after error
  //     if (chatId) {
  //       startAdminReconnectInterval(chatId);
  //     }
  //   };
  // };

  // const disconnectAdminSocket = () => {
  //   stopAdminPingInterval();
  //   stopAdminReconnectInterval();
  //   setManuallyDisconnected(true); // Mark as manually disconnected
  //   if (adminSocketRef.current) {
  //     adminSocketRef.current.close();
  //     adminSocketRef.current = null;
  //     setIsAdminConnected(false);
  //     setCurrentAdminChatId(null);
  //     console.log("Admin WebSocket manually disconnected.");
  //   } else {
  //     console.warn("No active admin WebSocket to disconnect.");
  //   }
  // };

  // ----------------------------- Ping Management -----------------------------

  // const startAdminPingInterval = () => {
  //   if (adminPingIntervalRef.current) {
  //     console.warn("Ping interval already running. Skipping new interval.");
  //     return;
  //   }

  //   // console.log("Starting admin ping interval");

  //   adminPingIntervalRef.current = setInterval(() => {
  //     const socket = adminSocketRef.current;
  //     if (socket && socket.readyState === WebSocket.OPEN) {
  //       // console.log("Sending ping to server");
  //       socket.send(JSON.stringify({ route: "ping" }));
  //     } else {
  //       console.warn("Cannot send ping - WebSocket not open");
  //       stopAdminPingInterval();
  //     }
  //   }, 30000);
  // };

  // const stopAdminPingInterval = () => {
  //   if (adminPingIntervalRef.current) {
  //     clearInterval(adminPingIntervalRef.current);
  //     adminPingIntervalRef.current = null;
  //     // console.log("Stopped admin ping interval");
  //   }
  // };

  // const startAdminReconnectInterval = (chatId) => {
  //   if (adminReconnectIntervalRef.current) {
  //     console.warn("Reconnect interval already running. Skipping new interval.");
  //     return;
  //   }

  //   console.log("Starting admin reconnect interval for chat:", chatId);

  //   adminReconnectIntervalRef.current = setInterval(() => {
  //     const socket = adminSocketRef.current;
  //     if (!socket || socket.readyState !== WebSocket.OPEN) {
  //       console.log("Admin connection lost, attempting to reconnect to chat:", chatId);
  //       // Clear the current socket reference
  //       adminSocketRef.current = null;
  //       setIsAdminConnected(false);
  //       // Attempt to reconnect only if we have a valid chatId
  //       if (chatId) {
  //         connectAdminSocket(chatId);
  //       }
  //     }
  //   }, 10000); // Check every 10 seconds
  // };

  // const stopAdminReconnectInterval = () => {
  //   if (adminReconnectIntervalRef.current) {
  //     clearInterval(adminReconnectIntervalRef.current);
  //     adminReconnectIntervalRef.current = null;
  //     console.log("Stopped admin reconnect interval");
  //   }
  // };

  // ----------------------------- Message Senders -----------------------------

  const sendMessage = (message) => {
    const socket = socketRef.current;
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify(message));
    } else {
      console.warn("Client WebSocket not connected. Cannot send message.");
    }
  };

  // const sendAdminMessage = (msg) => {
  //   const socket = adminSocketRef.current;
  //   if (socket && socket.readyState === WebSocket.OPEN) {
  //     // Get admin name from localStorage or use default
  //     const adminName = localStorage.getItem('username') || 'Admin';

  //     const messagePayload = {
  //       route: "admin_message",
  //       message: msg.message,
  //       sender: "Admin",
  //       admin_name: adminName,
  //       chat_id: currentAdminChatId
  //     };
  //     console.log("Sending admin message:", messagePayload);
  //     socket.send(JSON.stringify(messagePayload));
  //   } else {
  //     console.warn("Admin WebSocket is not open.");
  //   }
  // };

  // const getAdminConnectionStatus = () => {
  //   return adminSocketRef.current && adminSocketRef.current.readyState === WebSocket.OPEN;
  // };

  // const resetManualDisconnect = () => {
  //   setManuallyDisconnected(false);
  // };

  return (
    <WebSocketContext.Provider value={{ messages, sendMessage }}>
      {/* <AdminSocket.Provider value={{
        admin,
        sendAdminMessage,
        connectAdminSocket,
        setAdmin,
        disconnectAdminSocket,
        isAdminConnected,
        getAdminConnectionStatus,
        currentAdminChatId,
        manuallyDisconnected,
        resetManualDisconnect
      }}> */}
      {children}
      {/* </AdminSocket.Provider> */}
    </WebSocketContext.Provider>
  );
};
